import { RestClient } from "./restClient";

export type CatalogScope = "public" | "company" | "user";

export interface CatalogEntry {
  id: string;
  name: string;
  description: string;
  body: string;
  tags: string[];
  category: string;
  scope: CatalogScope;
  author_user_id: number;
  author_company_id: number;
  install_count: number;
  version: number;
  created_at: string;
  updated_at: string;
}

export interface CatalogListResponse {
  entries: CatalogEntry[];
  total: number;
}

export interface PublishSkillInput {
  name: string;
  description: string;
  body: string;
  tags?: string[];
  category?: string;
  scope?: CatalogScope;
}

export interface UpdateCatalogInput {
  description?: string;
  body?: string;
  tags?: string[];
  category?: string;
  scope?: CatalogScope;
}

export interface InstallSkillInput {
  tier?: "user" | "company";
}

export interface InstallResult {
  status: string;
  name: string;
  id: string;
}

export class SkillsMarketplaceApi {
  constructor(private client: RestClient) {}

  list(params?: {
    search?: string;
    category?: string;
    scope?: CatalogScope;
    limit?: number;
    offset?: number;
  }): Promise<CatalogListResponse> {
    const qs = new URLSearchParams();
    if (params?.search) qs.set("search", params.search);
    if (params?.category) qs.set("category", params.category);
    if (params?.scope) qs.set("scope", params.scope);
    if (params?.limit != null) qs.set("limit", String(params.limit));
    if (params?.offset != null) qs.set("offset", String(params.offset));
    const query = qs.toString() ? `?${qs.toString()}` : "";
    return this.client.get<CatalogListResponse>(`/core/v1/skills/marketplace${query}`);
  }

  get(id: string): Promise<CatalogEntry> {
    return this.client.get<CatalogEntry>(`/core/v1/skills/marketplace/${encodeURIComponent(id)}`);
  }

  publish(input: PublishSkillInput): Promise<CatalogEntry> {
    return this.client.json<CatalogEntry>("/core/v1/skills/marketplace", {
      method: "POST",
      body: JSON.stringify(input),
    });
  }

  update(id: string, input: UpdateCatalogInput): Promise<CatalogEntry> {
    return this.client.json<CatalogEntry>(`/core/v1/skills/marketplace/${encodeURIComponent(id)}`, {
      method: "PUT",
      body: JSON.stringify(input),
    });
  }

  install(id: string, input?: InstallSkillInput): Promise<InstallResult> {
    return this.client.json<InstallResult>(
      `/core/v1/skills/marketplace/${encodeURIComponent(id)}/install`,
      {
        method: "POST",
        body: JSON.stringify(input ?? { tier: "user" }),
      },
    );
  }

  async remove(id: string): Promise<void> {
    await this.client.raw(`/core/v1/skills/marketplace/${encodeURIComponent(id)}`, {
      method: "DELETE",
    });
  }
}
